<template>
	<div class="group-icon-picker" :class="{ large, disabled: !canUserEdit }">
		<div
			class="icon-picker-container"
			:class="{ uploading: isUploading }"
			@click="onClickContainer"
		>
			<UserGroupIcon
				v-if="!tempImageUrl && !selectedEmoji"
				type="solid"
				class="icon-picker-fallback"
			/>
			<img
				v-else-if="tempImageUrl"
				:src="tempImageUrl"
				class="icon-picker-image"
			/>
			<div
				v-else-if="selectedEmoji"
				class="icon-picker-emoji"
				v-text="selectedEmoji"
			/>
			<div class="icon-picker-uploading" v-if="isUploading">
				<Spinner />
			</div>
		</div>

		<Menu
			class="icon-picker-edit"
			:items="menuItems"
			left
			:offsetY="editOffset"
			@select="onSelectMenuItem"
			ref="menu"
			v-if="canUserEdit"
		>
			<PencilIcon type="regular" />
			<template #item="{ item }">
				<div class="icon-picker-menu-item">
					<span>{{ item.label }}</span>
					<component
						:is="item.icon"
						type="regular"
						class="icon-picker-menu-item-icon"
					/>
				</div>
			</template>
		</Menu>

		<FilePicker
			v-if="showFilePicker"
			@files="onFileSelect"
			accept="image/*"
			ref="filePicker"
		/>

		<EmojiPicker
			v-if="showEmojiPicker"
			@change="onSelectEmoji"
			skipSaveRecent
			:popoverProps="{ offsetY: emojiPickerOffset, left: true }"
			class="emoji-picker"
			ref="emojiPicker"
		/>
	</div>
</template>

<script setup>
import { ref, nextTick, useTemplateRef, markRaw, computed } from 'vue'
import {
	UserGroupIcon,
	PencilIcon,
	ImageIcon,
	FaceSmileIcon,
	TrashCanIcon,
} from '@/icons'
import { normalizeImageForFileKey, uploadDirect } from '@/api/upload'
import { imageResizePresets } from '@/helpers/constants'
import Menu from './Menu'
import Spinner from './Spinner'
import FilePicker from './FilePicker'
import EmojiPicker from './EmojiPicker'
import { useAlert } from './Alert/GlobalAlerts'
import { sleep } from '@/helpers/utils'
import { getChat } from '@/store'
import { chatCan, UPDATE_ICON } from '@/helpers/permissions'

const props = defineProps({
	chatId: {
		type: [Number, String],
		default: null,
	},
	imageUrl: {
		type: String,
		default: null,
	},
	emoji: {
		type: String,
		default: null,
	},
	skipFilePicker: {
		type: Boolean,
		default: false,
	},
	large: {
		type: Boolean,
		default: false,
	},
})

const chat = getChat(props.chatId)
const canUserEdit = props.chatId ? chatCan(chat, UPDATE_ICON) : true

const showFilePicker = ref(false)
const showEmojiPicker = ref(false)
const isUploading = ref(false)
const tempImageUrl = ref(props.imageUrl || chat?.icon_image_url || null)
const selectedEmoji = ref(props.emoji || chat?.icon_emoji || null)
const emit = defineEmits(['change', 'image', 'error', 'emoji', 'remove'])
const filePicker = useTemplateRef('filePicker')
const emojiPicker = useTemplateRef('emojiPicker')
const menu = useTemplateRef('menu')
const alert = useAlert()

const hasValue = computed(() => tempImageUrl.value || selectedEmoji.value)
const menuItems = computed(() => {
	return [
		{ id: 'image', label: 'Upload Photo', icon: markRaw(ImageIcon) },
		{ id: 'emoji', label: 'Emoji', icon: markRaw(FaceSmileIcon) },
		hasValue.value && {
			id: 'remove',
			label: 'Remove',
			danger: true,
			icon: markRaw(TrashCanIcon),
		},
	].filter(Boolean)
})
const emojiPickerOffset = computed(() => (props.large ? -55 : -42))
const editOffset = computed(() => (props.large ? -42 : -35))

async function onSelectMenuItem({ id }) {
	switch (id) {
		case 'image':
			onClickPhoto()
			break
		case 'emoji':
			onClickEmoji()
			break
		case 'remove':
			onClickRemove()
			break
		default:
			console.error(`Unknown menu item: ${id}`)
			break
	}
}

function onClickPhoto() {
	showFilePicker.value = true
	if (!props.skipFilePicker) {
		nextTick(() => {
			filePicker.value.openPicker()
		})
	}
}

async function onClickEmoji() {
	await sleep(50)
	showEmojiPicker.value = true
	nextTick(() => {
		emojiPicker.value.openPicker()
	})
}

function onClickRemove() {
	if (hasValue.value) {
		emit('remove')
		emit('change', {})
	}
	resetState()
}

async function onClickContainer() {
	if (!canUserEdit) return
	await sleep(50)
	menu.value.openMenu()
}

async function onFileSelect(files) {
	resetState()
	isUploading.value = true
	const file = files[0]
	if (!file.type?.startsWith('image/')) {
		alert.error('Please select a supported image format')
		isUploading.value = false
		return
	}

	try {
		tempImageUrl.value = URL.createObjectURL(file)
		const upload = await uploadDirect(file)
		const result = await normalizeImageForFileKey(
			upload.data.key,
			imageResizePresets.GROUP_ICON_IMAGE
		)
		emit('image', { ...result.normalized, file })
		emit('change', {
			icon_image_key: result.normalized.key,
		})
	} catch (e) {
		console.error(e)
		alert.error(e)
		emit('error', e)
		tempImageUrl.value = null
	}
	isUploading.value = false
}

function onSelectEmoji(emoji) {
	resetState()
	selectedEmoji.value = emoji.emoji
	emit('emoji', emoji)
	emit('change', { icon_emoji: emoji.emoji })
}

function resetState() {
	showFilePicker.value = false
	showEmojiPicker.value = false
	selectedEmoji.value = null
	tempImageUrl.value = null
	isUploading.value = false
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.group-icon-picker {
	--container-size: 75px;
	--icon-size: 45px;
	--edit-size: 35px;
	--border-radius: 16px;
	position: relative;
	width: min-content;
	&.large {
		--container-size: 92px;
		--icon-size: 55px;
		--edit-size: 42px;
		--border-radius: 20px;
	}
	&.disabled {
		.icon-picker-container {
			cursor: default;
		}
	}
	.icon-picker-container {
		cursor: pointer;
		position: relative;
		width: var(--container-size);
		height: var(--container-size);
		border-radius: var(--border-radius);
		background-color: var(--neutral-100);
		@include flex-center;
		justify-content: center;
		font-size: var(--icon-size);
		color: var(--neutral-500);
		overflow: hidden;
		&.uploading {
			> img {
				opacity: 0.5;
			}
		}
		> img {
			@include absolute-cover;
			width: 100%;
			height: 100%;
			object-fit: cover;
			border-radius: 20px;
		}
		.icon-picker-uploading {
			@include absolute-cover;
			@include flex-center;
			justify-content: center;
			.pronto-spinner {
				font-size: calc(var(--icon-size) - 8px);
			}
		}
		.icon-picker-emoji {
			font-size: var(--icon-size);
			width: var(--icon-size);
			height: var(--icon-size);
			margin-top: -12px;
		}
	}
	.icon-picker-edit {
		position: absolute;
		bottom: calc(var(--edit-size) / 4 * -1);
		right: calc(var(--edit-size) / 4 * -1);
		cursor: pointer;
		background-color: var(--teal-300);
		color: white;
		width: var(--edit-size);
		height: var(--edit-size);
		font-size: calc(var(--edit-size) / 2);
		border-radius: 50%;
		:deep(.popover-trigger) {
			width: var(--edit-size);
			height: var(--edit-size);
			@include flex-center;
			justify-content: center;
		}
		&:hover {
			filter: brightness(92%);
		}
	}
}
.icon-picker-menu-item {
	@include flex-spread;
	width: 180px;
	font-size: 16px;
	.icon-picker-menu-item-icon {
		font-size: 18px;
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.group-icon-picker {
		.icon-picker-container {
			background-color: var(--neutral-100);
		}
	}
}
</style>
